import { auth, db } from "../config/firebase";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  doc,
  DocumentData,
  getDoc,
  getDocs,
  setDoc,
  Timestamp,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { Filter } from "../utils/filter/classes";
import { Model } from "@beyondrealityapp/core/shared/constants";
import { PersonalityTestClass } from "@beyondrealityapp/core/personality_test/classes";
import {
  PersonalityTestForm,
  PersonalityTestFirestoreType,
} from "@beyondrealityapp/core/personality_test/types";
import { PersonalityTestField } from "@beyondrealityapp/core/personality_test/constants";

interface deletePersonalityTestArgs {
  personalityTestId: string;
}

export const personalityTestApi = createApi({
  reducerPath: "personalityTestApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["PersonalityTest"],
  endpoints: (builder) => ({
    fetchPersonalityTests: builder.query({
      queryFn: async (filter: Filter) => {
        try {
          const tests = await _retrievePersonalityTests(filter);
          return { data: tests };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "PersonalityTest" as const,
                id,
              })),
              "PersonalityTest",
            ]
          : ["PersonalityTest"],
    }),
    fetchPersonalityTest: builder.query({
      queryFn: async (id?: string) => {
        try {
          if (!id) {
            return { error: "No personality test id provided" };
          }
          const personalityTest = await _fetchPersonalityTest(id);
          return { data: personalityTest };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (result, error, arg) => [
        { type: "PersonalityTest", id: result?.id },
      ],
    }),
    createUpdatePersonalityTest: builder.mutation({
      queryFn: async (personalityTestForm: Partial<PersonalityTestForm>) => {
        try {
          const personalityTest =
            await _persistPersonalityTest(personalityTestForm);
          return { data: personalityTest };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "PersonalityTest", id: arg.id },
      ],
    }),
    deletePersonalityTest: builder.mutation({
      queryFn: async ({ personalityTestId }: deletePersonalityTestArgs) => {
        try {
          await _removePersonalityTest(personalityTestId);
          return { data: null };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "PersonalityTest", id: arg?.personalityTestId },
      ],
    }),
    updatePersonalityTestAnswers: builder.mutation({
      queryFn: async ({
        testId,
        answers,
      }: {
        testId: string;
        answers: { id: string; value: string }[];
      }) => {
        try {
          await _updatePersonalityTestAnswers(testId, answers);
          return { data: null };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "PersonalityTest", id: arg.testId },
      ],
    }),
  }),
});

export const {
  useFetchPersonalityTestsQuery,
  useFetchPersonalityTestQuery,
  useCreateUpdatePersonalityTestMutation,
  useDeletePersonalityTestMutation,
  useUpdatePersonalityTestAnswersMutation,
} = personalityTestApi;

const _removePersonalityTest = async (personalityTestId: string) => {
  await deleteDoc(doc(db, Model.PERSONALITY_TESTS, personalityTestId));
};

const _persistPersonalityTest = async (
  personalityTestForm: Partial<PersonalityTestForm>,
) => {
  const personalityTest = PersonalityTestClass.fromForm(personalityTestForm);
  personalityTest.init(auth);
  personalityTest.calculateStatus();

  await setDoc(
    doc(db, Model.PERSONALITY_TESTS, personalityTest.id),
    personalityTest.toFirestore(),
  );

  return personalityTest;
};

const _fetchPersonalityTest = async (id: string) => {
  const personalityTestDoc = await getDoc(doc(db, Model.PERSONALITY_TESTS, id));
  if (!personalityTestDoc.exists()) {
    throw new Error(`Personality test with id ${id} not found`);
  }
  return PersonalityTestClass.fromFirestore(
    personalityTestDoc.data() as PersonalityTestFirestoreType,
  );
};

const _updatePersonalityTestAnswers = async (
  testId: string,
  answers: { id: string; value: string }[],
) => {
  const testDoc = await getDoc(doc(db, Model.PERSONALITY_TESTS, testId));
  if (!testDoc.exists()) {
    throw new Error("Personality test not found");
  }

  const currentTest = testDoc.data();
  const updatedFields = currentTest.testFields.map((field: any) => {
    const answer = answers.find((a) => a.id === field.id);
    return answer ? { ...field, value: answer.value } : field;
  });

  const updatedTest = PersonalityTestClass.fromFirestore({
    ...currentTest,
    testFields: updatedFields,
  });
  updatedTest.calculateStatus();

  await updateDoc(doc(db, Model.PERSONALITY_TESTS, testId), {
    [PersonalityTestField.TEST_FIELDS]: updatedFields,
    [PersonalityTestField.STATUS]: updatedTest.status,
    [PersonalityTestField.UPDATED_AT]: Timestamp.now(),
  });
};

const _retrievePersonalityTests = async (filter: Filter) => {
  const q = filter.createQuery(db);
  const querySnapshot = await getDocs(q);
  const tests: PersonalityTestClass[] = [];

  querySnapshot.forEach((doc: DocumentData) => {
    const personalityTest = PersonalityTestClass.fromFirestore(
      doc.data() as PersonalityTestFirestoreType,
    );
    tests.push(personalityTest);
  });

  return tests;
};
